import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertCircle,
  ShieldCheck,
  Plus,
  Upload,
  FileText,
  Clock,
  PenLine,
  CheckCircle2,
  XCircle,
  LayoutTemplate,
  Archive,
  Trash2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetHRDocuments,
  apiGetEmployees,
  apiUploadHRDocumentFile,
  apiUpdateHRDocumentStatus,
  apiDeleteHRDocument,
} from "@/lib/api";
import type { HRDocument } from "@/lib/api";
import { DocumentStatusBadge } from "@/components/documents/DocumentStatusBadge";
import { DocumentTypeBadge } from "@/components/documents/DocumentTypeBadge";
import { DocumentViewModal } from "@/components/documents/DocumentViewModal";
import { RequestDocumentModal } from "@/components/documents/RequestDocumentModal";
import { RejectDocumentModal } from "@/components/documents/RejectDocumentModal";
import { UploadDocumentModal } from "@/components/documents/UploadDocumentModal";
import { DocumentTemplatesTab } from "@/components/documents/DocumentTemplatesTab";
import { TableSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";

const PAGE_SIZE = 15;
type StatusFilter = "all" | "Pending" | "Signed" | "Completed" | "Rejected";

// ── Stat strip ────────────────────────────────────────────────────────────────

interface StatItem {
  label: string;
  value: number;
  icon: React.ElementType;
  bg: string;
  color: string;
  filter: StatusFilter;
}

function StatStrip({
  items,
  active,
  onFilter,
}: {
  items: StatItem[];
  active: StatusFilter;
  onFilter: (f: StatusFilter) => void;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 sm:divide-x divide-border/60`}>
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.filter;
          return (
            <button
              key={item.filter}
              onClick={() => onFilter(isActive && item.filter !== "all" ? "all" : item.filter)}
              className={`flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/40 border-b sm:border-b-0 last:border-b-0 ${isActive ? "bg-muted/60" : ""}`}
            >
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${item.bg}`}>
                <Icon className={`h-4.5 w-4.5 ${item.color}`} style={{ height: "1.125rem", width: "1.125rem" }} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground leading-tight">{item.label}</p>
                <p className="text-xl font-bold text-foreground leading-tight">{item.value}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({
  page,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border/60">
      <span className="text-xs text-muted-foreground">
        {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground px-1">{page} / {totalPages}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages} onClick={() => onChange(page + 1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Section header (table title + badge count) ────────────────────────────────

function SectionLabel({
  icon: Icon,
  label,
  description,
  count,
  iconClass,
  badgeClass,
}: {
  icon: React.ElementType;
  label: string;
  description?: string;
  count: number;
  iconClass: string;
  badgeClass: string;
}) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-muted/20">
      <Icon className={`h-4 w-4 ${iconClass}`} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          {count > 0 && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}>
              {count}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

// ── Document table ────────────────────────────────────────────────────────────

function DocumentTable({
  docs,
  emptyMessage,
  showArchive,
  onView,
  onArchive,
  onDelete,
}: {
  docs: HRDocument[];
  emptyMessage: string;
  showArchive?: boolean;
  onView: (doc: HRDocument) => void;
  onArchive?: (doc: HRDocument) => void;
  onDelete?: (doc: HRDocument) => void;
}) {
  const { t } = useTranslation();

  const fmtDate = (raw: string) => {
    try { return format(new Date(raw), "MMM d, yyyy"); } catch { return raw; }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
          <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">
            {t("documents.documentName")}
          </TableHead>
          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">
            {t("documents.employeeLabel")}
          </TableHead>
          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">
            {t("documents.typeLabel")}
          </TableHead>
          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
            {t("documents.statusLabel")}
          </TableHead>
          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell">
            {t("documents.dateLabel")}
          </TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {docs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          docs.map((doc) => (
            <TableRow
              key={doc.id}
              className="border-b border-border/40 hover:bg-muted/40 transition-colors cursor-pointer"
              onClick={() => onView(doc)}
            >
              <TableCell className="pl-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{doc.documentName}</p>
                    {doc.gdprExpired && (
                      <p className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {t("documents.gdprExpiredShort")}
                      </p>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-3 hidden md:table-cell">
                <span className="text-sm text-muted-foreground">{doc.employeeName || "—"}</span>
              </TableCell>
              <TableCell className="py-3 hidden lg:table-cell">
                {doc.documentType ? <DocumentTypeBadge type={doc.documentType} /> : <span className="text-sm text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="py-3">
                <DocumentStatusBadge status={doc.status} />
              </TableCell>
              <TableCell className="py-3 hidden xl:table-cell">
                <span className="text-sm text-muted-foreground">{fmtDate(doc.uploadedAt)}</span>
              </TableCell>
              <TableCell className="py-3 w-20" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-0.5 justify-end pr-2">
                  {showArchive && onArchive && (doc.status === "Pending" || doc.status === "Signed") && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onArchive(doc)}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t("documents.markCompleted")}</TooltipContent>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onDelete(doc)}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t("common.delete")}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function Documents() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isHRAdmin =
    user?.role.mainRole === "admin" || user?.role.mainRole === "hr";

  useEffect(() => {
    if (user && !isHRAdmin) {
      navigate("/my-documents", { replace: true });
    }
  }, [user, isHRAdmin, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    return tab === "templates" ? "templates" : "documents";
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [actionPage, setActionPage] = useState(1);
  const [vaultPage, setVaultPage] = useState(1);
  const [viewDoc, setViewDoc] = useState<HRDocument | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [rejectDoc, setRejectDoc] = useState<HRDocument | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "request-sign") setRequestModalOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["hr-documents", selectedEmployeeId],
    queryFn: () => apiGetHRDocuments(selectedEmployeeId),
    enabled: Boolean(user) && isHRAdmin,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: isHRAdmin,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => apiUploadHRDocumentFile(id, file),
    onSuccess: (updated) => {
      handleDocumentUpdated(updated);
      toast.success(t("documents.messages.fileUploaded"), {
        description: t("documents.messages.fileUploadedDesc", { name: updated.documentName }),
      });
    },
    onError: (err: any) => toast.error(t("documents.messages.uploadFailed"), { description: err.message }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => apiUpdateHRDocumentStatus(id, "Completed"),
    onSuccess: (updated) => {
      handleDocumentUpdated(updated);
      toast.success(t("documents.messages.documentCompleted"), {
        description: t("documents.messages.documentCompletedDesc", { name: updated.documentName }),
      });
    },
    onError: (err: any) => toast.error(t("documents.messages.completeFailed"), { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteHRDocument(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<HRDocument[]>(
        ["hr-documents", selectedEmployeeId],
        (old = []) => old.filter((d) => d.id !== id),
      );
      toast.success(t("documents.messages.documentDeleted", { defaultValue: "Document deleted" }));
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDocumentUpdated = (updated: HRDocument) => {
    queryClient.setQueryData<HRDocument[]>(
      ["hr-documents", selectedEmployeeId],
      (old = []) => old.map((d) => (d.id === updated.id ? updated : d)),
    );
    setViewDoc((prev) => (prev?.id === updated.id ? updated : prev));
  };

  const handleDocumentCreated = (doc: HRDocument) => {
    if (selectedEmployeeId && doc.employeeId !== selectedEmployeeId) {
      queryClient.invalidateQueries({ queryKey: ["hr-documents"] });
      return;
    }
    queryClient.setQueryData<HRDocument[]>(
      ["hr-documents", selectedEmployeeId],
      (old = []) => [doc, ...old],
    );
  };

  const handleViewModalOpenChange = (open: boolean) => {
    setViewModalOpen(open);
    if (!open) setViewDoc(null);
  };

  const handleView = (doc: HRDocument) => {
    setViewDoc(doc);
    setViewModalOpen(true);
  };

  // Auto-open document from deep-link
  useEffect(() => {
    const openId = searchParams.get("openDocument");
    if (openId && documents.length > 0) {
      const target = documents.find((d) => d.id === Number(openId));
      if (target) { setViewDoc(target); setViewModalOpen(true); }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openDocument");
        return next;
      }, { replace: true });
    }
  }, [searchParams, documents, setSearchParams]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const stats = useMemo(() => ({
    total: documents.length,
    pending: documents.filter((d) => d.status === "Pending").length,
    signed: documents.filter((d) => d.status === "Signed").length,
    completed: documents.filter((d) => d.status === "Completed").length,
    rejected: documents.filter((d) => d.status === "Rejected").length,
  }), [documents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? documents.filter(
          (d) =>
            d.documentName.toLowerCase().includes(q) ||
            d.employeeName?.toLowerCase().includes(q) ||
            d.documentType?.toLowerCase().includes(q),
        )
      : documents;
  }, [documents, search]);

  const pendingDocs = useMemo(() => {
    const base = filtered.filter((d) => d.status === "Pending" || d.status === "Signed");
    if (statusFilter === "Pending") return base.filter((d) => d.status === "Pending");
    if (statusFilter === "Signed") return base.filter((d) => d.status === "Signed");
    return base;
  }, [filtered, statusFilter]);

  const vaultDocs = useMemo(() => {
    const base = filtered.filter((d) => d.status === "Completed" || d.status === "Rejected");
    if (statusFilter === "Completed") return base.filter((d) => d.status === "Completed");
    if (statusFilter === "Rejected") return base.filter((d) => d.status === "Rejected");
    return base;
  }, [filtered, statusFilter]);

  const pagedActionDocs = useMemo(
    () => pendingDocs.slice((actionPage - 1) * PAGE_SIZE, actionPage * PAGE_SIZE),
    [pendingDocs, actionPage],
  );

  const pagedVaultDocs = useMemo(
    () => vaultDocs.slice((vaultPage - 1) * PAGE_SIZE, vaultPage * PAGE_SIZE),
    [vaultDocs, vaultPage],
  );

  const handleFilterChange = (f: StatusFilter) => {
    setStatusFilter(f);
    setActionPage(1);
    setVaultPage(1);
  };

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ id: (e as any).id as number, name: e.name, jobTitle: e.jobTitle })),
    [employees],
  );

  const statItems: StatItem[] = [
    { label: t("documents.stat.total"),     value: stats.total,     icon: FileText,      bg: "bg-primary/10",                        color: "text-primary",       filter: "all"       },
    { label: t("documents.stat.pending"),   value: stats.pending,   icon: Clock,         bg: "bg-yellow-100 dark:bg-yellow-900/30",  color: "text-yellow-600",    filter: "Pending"   },
    { label: t("documents.stat.signed"),    value: stats.signed,    icon: PenLine,       bg: "bg-blue-100 dark:bg-blue-900/30",      color: "text-blue-600",      filter: "Signed"    },
    { label: t("documents.stat.completed"), value: stats.completed, icon: CheckCircle2,  bg: "bg-green-100 dark:bg-green-900/30",    color: "text-green-600",     filter: "Completed" },
    { label: t("documents.stat.rejected"),  value: stats.rejected,  icon: XCircle,       bg: "bg-red-100 dark:bg-red-900/30",        color: "text-red-600",       filter: "Rejected"  },
  ];

  if (!isHRAdmin) return null;

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("documents.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("documents.subtitleAdmin")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" id="upload-document-btn" onClick={() => setUploadModalOpen(true)}>
              <Upload className="h-4 w-4 mr-1.5" />
              {t("documents.uploadDocument")}
            </Button>
            <Button
              id="request-document-btn"
              onClick={() => setRequestModalOpen(true)}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t("documents.requestDocument")}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100"
        >
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="documents" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <FileText className="h-3.5 w-3.5" />
              {t("documents.tabs.documents")}
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <LayoutTemplate className="h-3.5 w-3.5" />
              {t("documents.tabs.templates")}
            </TabsTrigger>
          </TabsList>

          {/* ── Documents tab ─────────────────────────────────────────────── */}
          <TabsContent value="documents" className="mt-6 space-y-6">

            {/* Stats strip */}
            <StatStrip items={statItems} active={statusFilter} onFilter={handleFilterChange} />

            {/* Filters row */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setActionPage(1); setVaultPage(1); }}
                  placeholder={t("documents.documentName")}
                  className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20"
                />
              </div>

              {/* Employee filter */}
              <Select
                value={selectedEmployeeId ? String(selectedEmployeeId) : "all"}
                onValueChange={(v) => setSelectedEmployeeId(v === "all" ? undefined : Number(v))}
              >
                <SelectTrigger className="w-48 h-9 text-sm">
                  <SelectValue placeholder={t("documents.allEmployees")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("documents.allEmployees")}</SelectItem>
                  {employeeOptions.map((emp) => (
                    <SelectItem key={emp.id} value={String(emp.id)}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="space-y-6">
                <TableSkeleton rows={5} cols={5} />
                <TableSkeleton rows={3} cols={5} />
              </div>
            )}

            {/* Tables */}
            {!isLoading && (
              <div className="space-y-6">
                {/* Action Required */}
                <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <SectionLabel
                    icon={AlertCircle}
                    label={t("documents.actionRequired")}
                    description={t("documents.actionRequiredDescription")}
                    count={pendingDocs.length}
                    iconClass="text-yellow-500"
                    badgeClass="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                  />
                  <DocumentTable
                    docs={pagedActionDocs}
                    emptyMessage={t("documents.empty.noActionRequired")}
                    showArchive
                    onView={handleView}
                    onArchive={(doc) => completeMutation.mutate(doc.id)}
                    onDelete={(doc) => deleteMutation.mutate(doc.id)}
                  />
                  <Pagination
                    page={actionPage}
                    total={pendingDocs.length}
                    pageSize={PAGE_SIZE}
                    onChange={setActionPage}
                  />
                </div>

                {/* Document Vault */}
                <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 delay-75">
                  <SectionLabel
                    icon={ShieldCheck}
                    label={t("documents.documentVault")}
                    count={vaultDocs.length}
                    iconClass="text-green-500"
                    badgeClass="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  />
                  <DocumentTable
                    docs={pagedVaultDocs}
                    emptyMessage={t("documents.empty.noCompletedDocuments")}
                    onView={handleView}
                    onDelete={(doc) => deleteMutation.mutate(doc.id)}
                  />
                  <Pagination
                    page={vaultPage}
                    total={vaultDocs.length}
                    pageSize={PAGE_SIZE}
                    onChange={setVaultPage}
                  />
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Templates tab ─────────────────────────────────────────────── */}
          <TabsContent value="templates" className="mt-6">
            <DocumentTemplatesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <DocumentViewModal
        doc={viewDoc}
        open={viewModalOpen}
        onOpenChange={handleViewModalOpenChange}
        canSign={false}
        isHRAdmin={true}
        onDocumentUpdated={handleDocumentUpdated}
      />

      <UploadDocumentModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onDocumentCreated={handleDocumentCreated}
        employees={employeeOptions}
        defaultEmployeeId={selectedEmployeeId}
      />

      <RequestDocumentModal
        open={requestModalOpen}
        onOpenChange={setRequestModalOpen}
        employees={employeeOptions}
        onDocumentCreated={handleDocumentCreated}
        defaultEmployeeId={selectedEmployeeId}
      />

      {rejectDoc && (
        <RejectDocumentModal
          doc={rejectDoc}
          open={rejectModalOpen}
          onOpenChange={setRejectModalOpen}
          onDocumentUpdated={handleDocumentUpdated}
        />
      )}
    </AppLayout>
  );
}

export default Documents;
